import { FocusEvent, FunctionComponent, memo, useCallback, useMemo, useState } from 'react';

import { DateTimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { Validate, useController } from 'react-hook-form';

import { RhfValidators } from 'core/forms';
import { DateTimePicker } from 'core/ui';

interface CrossClampDateTimePickerProps {
  value?: Date | null;
  isSubProperty?: boolean;
  valueSetter?: (key: string, value: Date | null) => void;
  disabled?: boolean;
  name?: string;
}

interface CrossClampDateTimePickerFieldProps {
  name: string;
  required?: boolean;
  validator?: Validate<unknown, unknown>;
  onChange?: (event: DateTimePickerChangeEvent) => void;
  onBlur?: (event: FocusEvent<HTMLSpanElement | HTMLDivElement>) => void;
  disabled?: boolean;
}

export const CrossClampDateTimePicker: FunctionComponent<CrossClampDateTimePickerProps> = ({ value, isSubProperty, valueSetter, disabled }) => {
  // Ensure we have a proper Date object or null
  // const normalizedValue = value instanceof Date && !isNaN(value.getTime()) ? value : null;
  const [normalizedValue, setNormalizedValue] = useState<Date | null>(value instanceof Date && !isNaN(value.getTime()) ? value : null);

  const handleChange = (e: DateTimePickerChangeEvent) => {
    const newValue = e.value;

    const normalizedNewValue = newValue instanceof Date && !isNaN(newValue.getTime()) ? newValue : null;
    setNormalizedValue(normalizedNewValue);
    if (isSubProperty) {
      valueSetter?.('patient.crossClampDateTime', normalizedNewValue);
    } else {
      valueSetter?.('crossClampDateTime', normalizedNewValue);
    }
  };

  return <DateTimePicker label="Cross Clamp Time" value={normalizedValue} onChange={handleChange} disabled={disabled} format="M/d/yyyy h:mm a" />;
};

export const CrossClampDateTimePickerField = memo<CrossClampDateTimePickerFieldProps>(({ name, required, validator, onChange, onBlur, disabled }) => {
  const validatorInternal = useMemo(() => {
    const newValidators: Validate<unknown, unknown>[] = [];

    if (required) newValidators.push(RhfValidators.required);
    if (validator) newValidators.push(validator);

    return newValidators.length === 0 ? undefined : newValidators.length === 1 ? newValidators[0] : RhfValidators.combine(newValidators);
  }, [required, validator]);

  const {
    field: { onChange: rhfOnChange, onBlur: rhfOnBlur, value, ...fieldRest },
    formState,
    fieldState: { isTouched, invalid, error },
  } = useController({ name, rules: { validate: validatorInternal } });

  const handleChange = useCallback(
    (event: DateTimePickerChangeEvent) => {
      rhfOnChange(event.value);
      onChange?.(event);
    },
    [onChange, rhfOnChange],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLSpanElement | HTMLDivElement>) => {
      rhfOnBlur();
      onBlur?.(event);
    },
    [onBlur, rhfOnBlur],
  );

  // Ensure we have a proper Date object or null
  const normalizedValue = value instanceof Date && !isNaN(value.getTime()) ? value : null;

  return (
    <DateTimePicker
      {...fieldRest}
      label="Cross Clamp Time"
      value={normalizedValue}
      valid={!invalid}
      visited={formState.isSubmitted || isTouched}
      validationMessage={error?.message}
      required={required}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      format="M/d/yyyy h:mm a"
    />
  );
});

CrossClampDateTimePickerField.displayName = 'CrossClampDateTimePickerField';
